import React, { useState } from 'react';
import { Box, Typography, TextField, MenuItem, Button, Paper, Divider } from '@mui/material';
import { calculatePositionSize } from '../../api/tools';

const currencies = [
  { value: 'USD', label: 'USD' },
  { value: 'GBP', label: 'GBP' },
  { value: 'EUR', label: 'EUR' },
];

const formatNumber = (n) =>
  new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const PositionSizeCalculatorPage = () => {
  const [form, setForm] = useState({
    accountBalance: 10000,
    riskPercent: 0.5,
    entry: 10,
    stopLoss: 9.5,
    currency: 'USD',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onCalculate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        accountBalance: Number(form.accountBalance),
        riskPercent: Number(form.riskPercent),
        entry: Number(form.entry),
        stopLoss: Number(form.stopLoss),
        currency: form.currency,
      };
      const data = await calculatePositionSize(payload);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to calculate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.75rem', md: '2rem' },
            fontWeight: 600,
            letterSpacing: '-0.03em',
            color: 'var(--dwlf-text)',
            mb: 0.5,
          }}
        >
          Position Size Calculator
        </Typography>
        <Typography 
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.85rem',
            color: 'var(--dwlf-ink-50)',
          }}
        >
          Calculate position size based on risk parameters
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Box>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Account Balance"
                name="accountBalance"
                type="number"
                fullWidth
                value={form.accountBalance}
                onChange={handleChange}
                sx={{ gridColumn: '1 / -1' }}
              />
              <TextField
                label="Risk (%)"
                name="riskPercent"
                type="number"
                fullWidth
                value={form.riskPercent}
                onChange={handleChange}
              />
              <TextField
                select
                label="Currency"
                name="currency"
                fullWidth
                value={form.currency}
                onChange={handleChange}
              >
                {currencies.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Entry ($)"
                name="entry"
                type="number"
                fullWidth
                value={form.entry}
                onChange={handleChange}
              />
              <TextField
                label="Stop Loss ($)"
                name="stopLoss"
                type="number"
                fullWidth
                value={form.stopLoss}
                onChange={handleChange}
              />
              <Box sx={{ gridColumn: '1 / 2' }}>
                <Button variant="contained" onClick={onCalculate} disabled={loading}>
                  {loading ? 'Calculating...' : 'Calculate'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
        <Box>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Result</Typography>
            <Divider sx={{ my: 2 }} />
            {error && (
              <Typography color="error">{error}</Typography>
            )}
            {result && (
              <Box>
                <Typography variant="h3" sx={{ mb: 1 }}>{result.result.shares} shares</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  FX: {result.fx.pair} @ {formatNumber(result.fx.rate)}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle2">Tolerated Risk</Typography>
                    <Typography>${formatNumber(result.result.toleratedRiskUsd)} (USD)</Typography>
                    <Typography>{result.input.currency} {formatNumber(result.result.toleratedRiskAccountCcy)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Position Amount</Typography>
                    <Typography>${formatNumber(result.result.positionAmountUsd)} (USD)</Typography>
                    <Typography>{result.input.currency} {formatNumber(result.result.positionAmountAccountCcy)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Effective Risk</Typography>
                    <Typography>${formatNumber(result.result.effectiveRiskUsd)} (USD)</Typography>
                    <Typography>{result.input.currency} {formatNumber(result.result.effectiveRiskAccountCcy)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Per Share Risk</Typography>
                    <Typography>${formatNumber(result.result.perShareRiskUsd)} (USD)</Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {!result && !error && (
              <Typography variant="body2" color="text.secondary">Enter values and click Calculate.</Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default PositionSizeCalculatorPage;

